import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getUsers,
  setPage,
  setLimit,
  setFilters,
  setViewMode,
  deleteUser,
  clearError,
} from "../../features/users/usersSlice";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Pagination,
  Row,
  Table,
  Badge,
  Dropdown,
  Modal,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import UserFormModal from "../../components/users/UserFormModal";
import UserCard from "./components/UserCard";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import { Role, User } from "../../features/users/usersSlice";
import rolesService from "../../features/roles/rolesService";
import companiesService from "../../features/companies/companiesService";
import { Company } from "../../features/companies/companiesService";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { canManageUsers } from "../../utils/permissions";

const UsersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    users,
    isLoading,
    error,
    totalCount,
    page,
    limit,
    filters,
    viewMode,
  } = useAppSelector((state) => state.users);
  const { user } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // State for forms and modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // State for filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState<number | undefined>(undefined);
  const [companyFilter, setCompanyFilter] = useState<number | undefined>(
    undefined
  );

  // State for roles and companies (for filters and forms)
  const [roles, setRoles] = useState<Role[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Load users on component mount and when filters change
  useEffect(() => {
    dispatch(getUsers({ page, limit, ...filters }));
  }, [dispatch, page, limit, filters]);

  // Load roles and companies for filters
  useEffect(() => {
    const loadFiltersData = async () => {
      try {
        console.log("Loading roles and companies...");
        const rolesData = await rolesService.getRoles();
        console.log("Loaded roles:", rolesData);
        setRoles(rolesData);

        const companiesData = await companiesService.getCompanies({
          is_active: true,
        });
        console.log("Loaded companies:", companiesData);
        setCompanies(companiesData.companies);
      } catch (error) {
        console.error("Error loading filters data:", error);
        toast.error("Failed to load filter options");
      }
    };

    loadFiltersData();
  }, []);

  // Handle filter changes
  const handleFilterChange = () => {
    const newFilters = {
      status: statusFilter || undefined,
      role_id: roleFilter,
      company_id: companyFilter,
      search: searchTerm || undefined,
    };
    dispatch(setFilters(newFilters));
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setRoleFilter(undefined);
    setCompanyFilter(undefined);
    dispatch(setFilters({}));
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  // Handle rows per page change
  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setLimit(Number(e.target.value)));
    dispatch(setPage(1)); // Reset to first page
  };

  // Handle view mode change
  const handleViewModeChange = (mode: "list" | "grid") => {
    dispatch(setViewMode(mode));
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
  };

  // Close create modal
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  // Open edit modal
  const handleOpenEditModal = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setSelectedUser(null);
    setShowEditModal(false);
  };

  // Open delete modal
  const handleOpenDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setSelectedUser(null);
    setShowDeleteModal(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (statusFilter) count++;
    if (roleFilter) count++;
    if (companyFilter) count++;
    return count;
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await dispatch(deleteUser(selectedUser.id)).unwrap();
      toast.success("User deleted successfully");
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error("Failed to delete user");
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const baseClasses = "status-badge d-inline-flex align-items-center";

    switch (status.toLowerCase()) {
      case "active":
        return (
          <Badge className={`${baseClasses} status-active`}>
            <i className="bi bi-check-circle-fill me-1"></i>
            {t("active")}
          </Badge>
        );
      case "inactive":
        return (
          <Badge className={`${baseClasses} status-inactive`}>
            <i className="bi bi-pause-circle-fill me-1"></i>
            {t("inactive")}
          </Badge>
        );
      case "suspended":
        return (
          <Badge className={`${baseClasses} status-suspended`}>
            <i className="bi bi-exclamation-triangle-fill me-1"></i>
            {t("suspended")}
          </Badge>
        );
      case "deleted":
        return (
          <Badge className={`${baseClasses} status-deleted`}>
            <i className="bi bi-trash-fill me-1"></i>
            {t("deleted")}
          </Badge>
        );
      default:
        return (
          <Badge className={`${baseClasses} status-default`}>
            <i className="bi bi-info-circle-fill me-1"></i>
            {status}
          </Badge>
        );
    }
  };

  const getRoleBadges = (userRoles: string[]) => {
    return userRoles.map((roleKey) => (
      <Badge key={roleKey} className="role-badge me-1">
        <i className="bi bi-person-badge-fill me-1"></i>
        {roleKey}
      </Badge>
    ));
  };

  // getCompanyBadges fonksiyonu şöyle olmalı:
  const getCompanyBadges = (
    userCompanies: { company_id: number; role?: string }[]
  ) => {
    return userCompanies
      .map((userCompany) => {
        const company = companies.find((c) => c.id === userCompany.company_id);
        return company ? (
          <Badge key={company.id} className="company-badge me-1">
            <i className="bi bi-building-fill me-1"></i>
            {company.company_name}
          </Badge>
        ) : null;
      })
      .filter(Boolean);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / limit);
  const paginationItems = [];

  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <Pagination.Item
        key={i}
        active={i === page}
        onClick={() => handlePageChange(i)}
      >
        {i}
      </Pagination.Item>
    );
  }

  return (
    <>
      <Container fluid className="users-page py-4">
        {/* Enhanced Header */}
        <Row className="mb-5">
          <Col>
            <div className="page-header">
              <div className="header-content">
                <div className="header-icon">
                  <i className="bi bi-people-fill"></i>
                </div>
                <div className="header-text">
                  <h1 className="page-title mb-2">{t("user_management")}</h1>
                  <p className="page-subtitle mb-0">
                    {t("manage_system_users_roles_and_permissions")}
                  </p>
                </div>
              </div>
              <div className="header-stats">
                <div className="stat-item">
                  <div className="stat-value">{totalCount}</div>
                  <div className="stat-label">{t("total_users")}</div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <div className="stat-value">
                    {users.filter((u) => u.status === "active").length}
                  </div>
                  <div className="stat-label">{t("active")}</div>
                </div>
              </div>
            </div>
          </Col>
          <Col xs="auto" className="d-flex align-items-center">
            <div className="action-controls">
              <div className="view-toggle me-3">
                <Button
                  variant={viewMode === "list" ? "primary" : "outline-primary"}
                  onClick={() => handleViewModeChange("list")}
                  className="view-btn"
                >
                  <i className="bi bi-list-ul"></i>
                </Button>
                <Button
                  variant={viewMode === "grid" ? "primary" : "outline-primary"}
                  onClick={() => handleViewModeChange("grid")}
                  className="view-btn"
                >
                  <i className="bi bi-grid-3x3-gap"></i>
                </Button>
              </div>
              <Button
                variant="primary"
                onClick={handleOpenCreateModal}
                className="create-btn"
              >
                <i className="bi bi-plus-circle-fill me-2"></i>
                <span>{t("add_user")}</span>
              </Button>
            </div>
          </Col>
        </Row>

        {/* Enhanced Filters */}
        <Card className="filters-card mb-4">
          <Card.Header className="filters-header">
            <div className="filters-title">
              <div className="title-icon">
                <i className="bi bi-funnel-fill"></i>
              </div>
              <h6 className="title-text mb-0">{t("filters")}</h6>
              <Badge className="filters-count">{getActiveFiltersCount()}</Badge>
            </div>
            <Button
              variant="link"
              size="sm"
              onClick={handleResetFilters}
              className="reset-all-btn"
              disabled={getActiveFiltersCount() === 0}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              {t("reset_all")}
            </Button>
          </Card.Header>

          <Card.Body className="filters-body">
            <Row className="g-4">
              {/* Search Field */}
              <Col lg={4} md={6} xs={12}>
                <Form.Group className="filter-group">
                  <Form.Label className="filter-label">
                    <i className="bi bi-search me-2"></i>
                    {t("search")}
                  </Form.Label>
                  <InputGroup className="search-input">
                    <InputGroup.Text className="search-icon">
                      <i className="bi bi-search"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={t("search_placeholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-field"
                    />
                    {searchTerm && (
                      <Button
                        variant="link"
                        className="clear-search"
                        onClick={() => setSearchTerm("")}
                      >
                        <i className="bi bi-x-lg"></i>
                      </Button>
                    )}
                  </InputGroup>
                </Form.Group>
              </Col>

              {/* Status Filter */}
              <Col lg={2} md={6} xs={6}>
                <Form.Group className="filter-group">
                  <Form.Label className="filter-label">
                    <i className="bi bi-toggle2-on me-2"></i>
                    {t("status")}
                  </Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">{t("all_statuses")}</option>
                    <option value="active">{t("active")}</option>
                    <option value="inactive">{t("inactive")}</option>
                    <option value="suspended">{t("suspended")}</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Role Filter */}
              <Col lg={2} md={6} xs={6}>
                <Form.Group className="filter-group">
                  <Form.Label className="filter-label">
                    <i className="bi bi-person-badge me-2"></i>
                    {t("role")}
                  </Form.Label>
                  <Form.Select
                    value={roleFilter || ""}
                    onChange={(e) =>
                      setRoleFilter(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    className="filter-select"
                  >
                    <option value="">{t("all_roles")}</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Company Filter */}
              <Col lg={3} md={6} xs={12}>
                <Form.Group className="filter-group">
                  <Form.Label className="filter-label">
                    <i className="bi bi-building me-2"></i>
                    {t("company")}
                  </Form.Label>
                  <Form.Select
                    value={companyFilter || ""}
                    onChange={(e) =>
                      setCompanyFilter(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    className="filter-select"
                  >
                    <option value="">{t("all_companies")}</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Action Buttons */}
              <Col lg={1} md={6} xs={12} className="d-flex align-items-end">
                <div className="filter-actions w-100">
                  <Button
                    variant="primary"
                    onClick={handleFilterChange}
                    className="apply-btn w-100 mb-2"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Spinner size="sm" className="me-2" />
                    ) : (
                      <i className="bi bi-check2-circle me-2"></i>
                    )}
                    {t("apply")}
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Active Filters Display */}
            {getActiveFiltersCount() > 0 && (
              <div className="active-filters mt-4">
                <div className="active-filters-label">
                  <i className="bi bi-funnel me-2"></i>
                  {t("active_filters")}:
                </div>
                <div className="active-filters-list">
                  {searchTerm && (
                    <div className="filter-tag filter-tag-search">
                      <i className="bi bi-search me-1"></i>"{searchTerm}"
                      <button
                        className="filter-tag-remove"
                        onClick={() => setSearchTerm("")}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  )}

                  {statusFilter && (
                    <div className="filter-tag filter-tag-status">
                      <i className="bi bi-toggle2-on me-1"></i>
                      {t(statusFilter)}
                      <button
                        className="filter-tag-remove"
                        onClick={() => setStatusFilter("")}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  )}

                  {roleFilter && (
                    <div className="filter-tag filter-tag-role">
                      <i className="bi bi-person-badge me-1"></i>
                      {roles.find((r) => r.id === roleFilter)?.name}
                      <button
                        className="filter-tag-remove"
                        onClick={() => setRoleFilter(undefined)}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  )}

                  {companyFilter && (
                    <div className="filter-tag filter-tag-company">
                      <i className="bi bi-building me-1"></i>
                      {
                        companies.find((c) => c.id === companyFilter)
                          ?.company_name
                      }
                      <button
                        className="filter-tag-remove"
                        onClick={() => setCompanyFilter(undefined)}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Error display */}
        {error && (
          <ErrorAlert message={error} onClose={() => dispatch(clearError())} />
        )}

        {/* Loading indicator */}
        {isLoading && <LoadingSpinner />}

        {/* Users list view */}
        {!isLoading && viewMode === "list" && (
          <Card className="data-table-card">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="users-table mb-0">
                  <thead className="table-header">
                    <tr>
                      <th className="table-th">
                        <div className="th-content">
                          <i className="bi bi-person me-2"></i>
                          {t("name")}
                        </div>
                      </th>
                      <th className="table-th">
                        <div className="th-content">
                          <i className="bi bi-at me-2"></i>
                          {t("username")}
                        </div>
                      </th>
                      <th className="table-th">
                        <div className="th-content">
                          <i className="bi bi-envelope me-2"></i>
                          {t("email")}
                        </div>
                      </th>
                      <th className="table-th">
                        <div className="th-content">
                          <i className="bi bi-toggle2-on me-2"></i>
                          {t("status")}
                        </div>
                      </th>
                      <th className="table-th">
                        <div className="th-content">
                          <i className="bi bi-person-badge me-2"></i>
                          {t("roles")}
                        </div>
                      </th>
                      <th className="table-th">
                        <div className="th-content">
                          <i className="bi bi-calendar-event me-2"></i>
                          {t("created_at")}
                        </div>
                      </th>
                      <th className="table-th text-end">
                        <div className="th-content justify-content-end">
                          <i className="bi bi-gear me-2"></i>
                          {t("actions")}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {users.length > 0 ? (
                      users.map((user, index) => (
                        <tr
                          key={user.id}
                          className={`table-row ${index % 2 === 0 ? "row-even" : "row-odd"}`}
                        >
                          <td className="table-cell">
                            <div className="user-info">
                              <div className="user-avatar">
                                <div className="avatar-circle">
                                  <span className="avatar-text">
                                    {user.full_name?.charAt(0)?.toUpperCase() ||
                                      "U"}
                                  </span>
                                </div>
                                <div className="avatar-status"></div>
                              </div>
                              <div className="user-details">
                                <div className="user-name">
                                  {user.full_name}
                                </div>
                                <div className="user-id">ID: {user.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="username-badge">
                              <i className="bi bi-at me-1"></i>
                              {user.username}
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="email-display">
                              <i className="bi bi-envelope me-2"></i>
                              {user.email}
                            </div>
                          </td>
                          <td className="table-cell">
                            {getStatusBadge(user.status)}
                          </td>
                          <td className="table-cell">
                            <div className="roles-container">
                              {getRoleBadges(user.roles)}
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="date-display">
                              <div className="date-primary">
                                {new Date(user.created_at).toLocaleDateString()}
                              </div>
                              <div className="date-secondary">
                                {new Date(user.created_at).toLocaleTimeString()}
                              </div>
                            </div>
                          </td>
                          <td className="table-cell text-end">
                            <div className="action-buttons">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="action-btn edit-btn"
                                onClick={() => handleOpenEditModal(user)}
                                title={t("edit_user")}
                              >
                                <i className="bi bi-pencil-square"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="action-btn delete-btn"
                                onClick={() => handleOpenDeleteModal(user)}
                                title={t("delete_user")}
                              >
                                <i className="bi bi-trash3"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="empty-state">
                          <div className="empty-content">
                            <div className="empty-icon">
                              <i className="bi bi-people"></i>
                            </div>
                            <h5 className="empty-title">
                              {t("no_users_found")}
                            </h5>
                            <p className="empty-description">
                              {t("no_users_description")}
                            </p>
                            <Button
                              variant="primary"
                              onClick={handleOpenCreateModal}
                              className="empty-action"
                            >
                              <i className="bi bi-plus-circle me-2"></i>
                              {t("add_first_user")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Users grid view */}
        {!isLoading && viewMode === "grid" && (
          <div className="users-grid">
            {users.length > 0 ? (
              <Row className="g-4">
                {users.map((user) => (
                  <Col key={user.id} xl={3} lg={4} md={6} sm={12}>
                    <UserCard
                      user={user}
                      onEdit={() => handleOpenEditModal(user)}
                      onDelete={() => handleOpenDeleteModal(user)}
                      getStatusBadge={getStatusBadge}
                      getRoleBadges={getRoleBadges}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="grid-empty-state">
                <div className="empty-content">
                  <div className="empty-icon">
                    <i className="bi bi-people"></i>
                  </div>
                  <h5 className="empty-title">{t("no_users_found")}</h5>
                  <p className="empty-description">
                    {t("no_users_description")}
                  </p>
                  <Button
                    variant="primary"
                    onClick={handleOpenCreateModal}
                    className="empty-action"
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    {t("add_first_user")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Enhanced Pagination */}
        {!isLoading && users.length > 0 && (
          <Card className="pagination-card mt-4">
            <Card.Body className="pagination-body">
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="pagination-info">
                    <div className="rows-per-page">
                      <span className="label">{t("rows_per_page")}:</span>
                      <Form.Select
                        value={limit}
                        onChange={handleLimitChange}
                        className="page-size-select"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                      </Form.Select>
                    </div>
                    <div className="showing-info">
                      {t("showing")}{" "}
                      <span className="highlight">
                        {(page - 1) * limit + 1}
                      </span>{" "}
                      {t("to")}{" "}
                      <span className="highlight">
                        {Math.min(page * limit, totalCount)}
                      </span>{" "}
                      {t("of")} <span className="highlight">{totalCount}</span>{" "}
                      {t("entries")}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="pagination-controls">
                    <Pagination className="custom-pagination mb-0">
                      <Pagination.First
                        onClick={() => handlePageChange(1)}
                        disabled={page === 1}
                        className="pagination-item"
                      />
                      <Pagination.Prev
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className="pagination-item"
                      />

                      {paginationItems}

                      <Pagination.Next
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className="pagination-item"
                      />
                      <Pagination.Last
                        onClick={() => handlePageChange(totalPages)}
                        disabled={page === totalPages}
                        className="pagination-item"
                      />
                    </Pagination>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* User Form Modal */}
        <UserFormModal
          show={showCreateModal || showEditModal}
          onHide={(refresh) => {
            if (refresh) {
              dispatch(getUsers({ page, limit, ...filters }));
            }
            handleCloseCreateModal();
            handleCloseEditModal();
          }}
                user={selectedUser}
                roles={roles}
                companies={companies}
              />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          show={showDeleteModal}
          onCancel={handleCloseDeleteModal}
          onConfirm={handleDeleteUser}
          title={t("delete_user")}
          message={`${t("are_you_sure_you_want_to_delete")} ${selectedUser?.full_name}? ${t("this_action_cannot_be_undone")}`}
          confirmText={t("delete")}
        />
      </Container>

      {/* Theme-Aware Styles */}
      <style>{`
        .users-page {
          min-height: 100vh;
          background: ${
            isDark
              ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
              : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)"
          };
        }

        /* Enhanced Header Styles */
        .page-header {
          background: ${
            isDark ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.9)"
          };
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 2rem;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"};
          box-shadow: ${
            isDark
              ? "0 20px 40px rgba(0, 0, 0, 0.3)"
              : "0 20px 40px rgba(0, 0, 0, 0.08)"
          };
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .header-icon {
          width: 60px;
          height: 60px;
          border-radius: 16px;
          background: ${
            isDark
              ? "linear-gradient(135deg, #3b82f6, #1d4ed8)"
              : "linear-gradient(135deg, #60a5fa, #3b82f6)"
          };
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: ${isDark ? "#f1f5f9" : "#1e293b"};
          margin: 0;
          background: ${
            isDark
              ? "linear-gradient(135deg, #f1f5f9, #cbd5e1)"
              : "linear-gradient(135deg, #1e293b, #475569)"
          };
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .page-subtitle {
          color: ${isDark ? "#94a3b8" : "#64748b"};
          font-size: 1rem;
          font-weight: 500;
        }

        .header-stats {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .stat-item {
          text-align: center;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: ${isDark ? "#60a5fa" : "#3b82f6"};
          line-height: 1;
        }

        .stat-label {
          font-size: 0.75rem;
          color: ${isDark ? "#94a3b8" : "#64748b"};
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 0.25rem;
        }

        .stat-divider {
          width: 1px;
          height: 40px;
          background: ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          };
        }

        /* Action Controls */
        .action-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .view-toggle {
          display: flex;
          background: ${
            isDark ? "rgba(51, 65, 85, 0.6)" : "rgba(248, 250, 252, 0.8)"
          };
          border-radius: 12px;
          padding: 4px;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"};
        }

        .view-btn {
          border: none !important;
          border-radius: 8px !important;
          padding: 0.5rem 1rem !important;
          font-weight: 500 !important;
          transition: all 0.3s ease !important;
        }

        .create-btn {
          background: linear-gradient(135deg, #10b981, #059669) !important;
          border: none !important;
          border-radius: 12px !important;
          padding: 0.75rem 1.5rem !important;
          font-weight: 600 !important;
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3) !important;
          transition: all 0.3s ease !important;
        }

        .create-btn:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 12px 30px rgba(16, 185, 129, 0.4) !important;
        }

        /* Enhanced Filters Card */
        .filters-card {
          background: ${
            isDark ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.95)"
          } !important;
          backdrop-filter: blur(20px) !important;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"} !important;
          border-radius: 20px !important;
          box-shadow: ${
            isDark
              ? "0 20px 40px rgba(0, 0, 0, 0.3)"
              : "0 20px 40px rgba(0, 0, 0, 0.08)"
          } !important;
        }

        .filters-header {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.4)" : "rgba(248, 250, 252, 0.8)"
          } !important;
          border-bottom: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"} !important;
          border-radius: 20px 20px 0 0 !important;
          padding: 1.5rem 2rem !important;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .filters-title {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .title-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: ${
            isDark
              ? "linear-gradient(135deg, #8b5cf6, #7c3aed)"
              : "linear-gradient(135deg, #a78bfa, #8b5cf6)"
          };
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 0.9rem;
        }

        .title-text {
          color: ${isDark ? "#f1f5f9" : "#1e293b"};
          font-weight: 600;
          font-size: 1.1rem;
        }

        .filters-count {
          background: ${
            isDark
              ? "linear-gradient(135deg, #f59e0b, #d97706)"
              : "linear-gradient(135deg, #fbbf24, #f59e0b)"
          } !important;
          color: white !important;
          border-radius: 20px !important;
          padding: 0.25rem 0.75rem !important;
          font-weight: 600 !important;
          font-size: 0.8rem !important;
        }

        .reset-all-btn {
          color: ${isDark ? "#94a3b8" : "#64748b"} !important;
          text-decoration: none !important;
          font-weight: 500 !important;
          transition: all 0.3s ease !important;
        }

        .reset-all-btn:hover {
          color: ${isDark ? "#f59e0b" : "#d97706"} !important;
        }

        .filters-body {
          padding: 2rem !important;
        }

        /* Filter Groups */
        .filter-group {
          margin-bottom: 0 !important;
        }

        .filter-label {
          color: ${isDark ? "#e2e8f0" : "#374151"} !important;
          font-weight: 600 !important;
          font-size: 0.9rem !important;
          margin-bottom: 0.75rem !important;
          display: flex;
          align-items: center;
        }

        .search-input {
          border-radius: 12px !important;
          overflow: hidden;
          box-shadow: ${
            isDark
              ? "0 4px 12px rgba(0, 0, 0, 0.2)"
              : "0 4px 12px rgba(0, 0, 0, 0.05)"
          } !important;
        }

        .search-icon {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.8)" : "rgba(248, 250, 252, 0.9)"
          } !important;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} !important;
          color: ${isDark ? "#94a3b8" : "#64748b"} !important;
        }

        .search-field {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.6)" : "rgba(255, 255, 255, 0.9)"
          } !important;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} !important;
          color: ${isDark ? "#f1f5f9" : "#1e293b"} !important;
        }

        .search-field::placeholder {
          color: ${isDark ? "#94a3b8" : "#64748b"} !important;
        }

        .clear-search {
          color: ${isDark ? "#94a3b8" : "#64748b"} !important;
          border: none !important;
          background: transparent !important;
          padding: 0.5rem !important;
        }

        .filter-select {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.6)" : "rgba(255, 255, 255, 0.9)"
          } !important;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} !important;
          border-radius: 12px !important;
          color: ${isDark ? "#f1f5f9" : "#1e293b"} !important;
          padding: 0.75rem 1rem !important;
          box-shadow: ${
            isDark
              ? "0 4px 12px rgba(0, 0, 0, 0.2)"
              : "0 4px 12px rgba(0, 0, 0, 0.05)"
          } !important;
        }

        .apply-btn {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8) !important;
          border: none !important;
          border-radius: 12px !important;
          font-weight: 600 !important;
          padding: 0.75rem 1rem !important;
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.3) !important;
          transition: all 0.3s ease !important;
        }

        .apply-btn:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4) !important;
        }

        /* Active Filters */
        .active-filters {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.3)" : "rgba(248, 250, 252, 0.6)"
          };
          border-radius: 12px;
          padding: 1rem;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"};
        }

        .active-filters-label {
          color: ${isDark ? "#94a3b8" : "#64748b"};
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
        }

        .active-filters-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .filter-tag {
          background: ${
            isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)"
          };
          color: ${isDark ? "#93c5fd" : "#1d4ed8"};
          border: 1px solid
            ${isDark ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.2)"};
          border-radius: 20px;
          padding: 0.375rem 0.75rem;
          font-size: 0.8rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          transition: all 0.3s ease;
        }

        .filter-tag-remove {
          background: transparent;
          border: none;
          color: inherit;
          padding: 0;
          margin-left: 0.25rem;
          cursor: pointer;
          opacity: 0.7;
          transition: opacity 0.3s ease;
        }

        .filter-tag-remove:hover {
          opacity: 1;
        }

        .filter-tag-search {
          background: ${
            isDark ? "rgba(16, 185, 129, 0.2)" : "rgba(16, 185, 129, 0.1)"
          };
          color: ${isDark ? "#6ee7b7" : "#059669"};
          border-color: ${
            isDark ? "rgba(16, 185, 129, 0.3)" : "rgba(16, 185, 129, 0.2)"
          };
        }

        .filter-tag-status {
          background: ${
            isDark ? "rgba(245, 158, 11, 0.2)" : "rgba(245, 158, 11, 0.1)"
          };
          color: ${isDark ? "#fcd34d" : "#d97706"};
          border-color: ${
            isDark ? "rgba(245, 158, 11, 0.3)" : "rgba(245, 158, 11, 0.2)"
          };
        }

        .filter-tag-role {
          background: ${
            isDark ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"
          };
          color: ${isDark ? "#c4b5fd" : "#7c3aed"};
          border-color: ${
            isDark ? "rgba(139, 92, 246, 0.3)" : "rgba(139, 92, 246, 0.2)"
          };
        }

        .filter-tag-company {
          background: ${
            isDark ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.1)"
          };
          color: ${isDark ? "#fca5a5" : "#dc2626"};
          border-color: ${
            isDark ? "rgba(239, 68, 68, 0.3)" : "rgba(239, 68, 68, 0.2)"
          };
        }

        /* Data Table Styles */
        .data-table-card {
          background: ${
            isDark ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.95)"
          } !important;
          backdrop-filter: blur(20px) !important;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"} !important;
          border-radius: 20px !important;
          box-shadow: ${
            isDark
              ? "0 20px 40px rgba(0, 0, 0, 0.3)"
              : "0 20px 40px rgba(0, 0, 0, 0.08)"
          } !important;
          overflow: hidden;
        }

        .users-table {
          color: ${isDark ? "#f1f5f9" : "#1e293b"} !important;
        }

        .table-header {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.6)" : "rgba(248, 250, 252, 0.9)"
          } !important;
        }

        .table-th {
          border: none !important;
          padding: 1.25rem 1.5rem !important;
          font-weight: 600 !important;
          font-size: 0.9rem !important;
          color: ${isDark ? "#e2e8f0" : "#374151"} !important;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .th-content {
          display: flex;
          align-items: center;
        }

        .table-body {
          background: transparent;
        }

        .table-row {
          border-bottom: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"} !important;
          transition: all 0.3s ease;
        }

        .table-row:hover {
          background: ${
            isDark ? "rgba(59, 130, 246, 0.05)" : "rgba(59, 130, 246, 0.02)"
          } !important;
        }

        .row-even {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.2)" : "rgba(248, 250, 252, 0.3)"
          } !important;
        }

        .table-cell {
          padding: 1.25rem 1.5rem !important;
          border: none !important;
          vertical-align: middle !important;
        }

        /* User Info Styles */
        .user-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .user-avatar {
          position: relative;
        }

        .avatar-circle {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .avatar-text {
          color: white;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .avatar-status {
          position: absolute;
          bottom: -2px;
          right: -2px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #10b981;
          border: 3px solid ${isDark ? "#1e293b" : "#ffffff"};
        }

        .user-details {
          flex: 1;
        }

        .user-name {
          font-weight: 600;
          font-size: 1rem;
          color: ${isDark ? "#f1f5f9" : "#1e293b"};
          margin-bottom: 0.25rem;
        }

        .user-id {
          font-size: 0.8rem;
          color: ${isDark ? "#94a3b8" : "#64748b"};
        }

        .username-badge {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.6)" : "rgba(248, 250, 252, 0.8)"
          };
          color: ${isDark ? "#cbd5e1" : "#475569"};
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"};
          border-radius: 8px;
          padding: 0.375rem 0.75rem;
          font-size: 0.85rem;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
        }

        .email-display {
          color: ${isDark ? "#cbd5e1" : "#475569"};
          font-size: 0.9rem;
          display: flex;
          align-items: center;
        }

        /* Status Badges */
        .status-badge {
          border-radius: 20px !important;
          padding: 0.375rem 0.75rem !important;
          font-size: 0.8rem !important;
          font-weight: 600 !important;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-active {
          background: rgba(16, 185, 129, 0.1) !important;
          color: ${isDark ? "#6ee7b7" : "#059669"} !important;
          border: 1px solid rgba(16, 185, 129, 0.2) !important;
        }

        .status-inactive {
          background: rgba(107, 114, 128, 0.1) !important;
          color: ${isDark ? "#d1d5db" : "#374151"} !important;
          border: 1px solid rgba(107, 114, 128, 0.2) !important;
        }

        .status-suspended {
          background: rgba(245, 158, 11, 0.1) !important;
          color: ${isDark ? "#fcd34d" : "#d97706"} !important;
          border: 1px solid rgba(245, 158, 11, 0.2) !important;
        }

        .status-deleted {
          background: rgba(239, 68, 68, 0.1) !important;
          color: ${isDark ? "#fca5a5" : "#dc2626"} !important;
          border: 1px solid rgba(239, 68, 68, 0.2) !important;
        }

        .status-default {
          background: rgba(59, 130, 246, 0.1) !important;
          color: ${isDark ? "#93c5fd" : "#1d4ed8"} !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
        }

        /* Role and Company Badges */
        .role-badge {
          background: rgba(139, 92, 246, 0.1) !important;
          color: ${isDark ? "#c4b5fd" : "#7c3aed"} !important;
          border: 1px solid rgba(139, 92, 246, 0.2) !important;
          border-radius: 16px !important;
          padding: 0.25rem 0.5rem !important;
          font-size: 0.75rem !important;
          font-weight: 500 !important;
          display: inline-flex;
          align-items: center;
        }

        .company-badge {
          background: rgba(239, 68, 68, 0.1) !important;
          color: ${isDark ? "#fca5a5" : "#dc2626"} !important;
          border: 1px solid rgba(239, 68, 68, 0.2) !important;
          border-radius: 16px !important;
          padding: 0.25rem 0.5rem !important;
          font-size: 0.75rem !important;
          font-weight: 500 !important;
          display: inline-flex;
          align-items: center;
        }

        .roles-container {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem;
        }

        /* Date Display */
        .date-display {
          text-align: left;
        }

        .date-primary {
          font-weight: 500;
          color: ${isDark ? "#f1f5f9" : "#1e293b"};
          font-size: 0.9rem;
        }

        .date-secondary {
          font-size: 0.75rem;
          color: ${isDark ? "#94a3b8" : "#64748b"};
          margin-top: 0.125rem;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
        }

        .action-btn {
          border-radius: 8px !important;
          padding: 0.5rem !important;
          width: 36px;
          height: 36px;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.3s ease !important;
          font-size: 0.9rem;
        }

        .edit-btn:hover {
          background: rgba(16, 185, 129, 0.1) !important;
          border-color: #10b981 !important;
          color: #10b981 !important;
          transform: translateY(-1px);
        }

        .delete-btn:hover {
          background: rgba(239, 68, 68, 0.1) !important;
          border-color: #ef4444 !important;
          color: #ef4444 !important;
          transform: translateY(-1px);
        }

        /* Empty State */
        .empty-state {
          padding: 4rem 2rem !important;
          text-align: center;
        }

        .empty-content {
          max-width: 400px;
          margin: 0 auto;
        }

        .empty-icon {
          font-size: 4rem;
          color: ${isDark ? "#64748b" : "#94a3b8"};
          margin-bottom: 1.5rem;
        }

        .empty-title {
          color: ${isDark ? "#e2e8f0" : "#374151"};
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .empty-description {
          color: ${isDark ? "#94a3b8" : "#64748b"};
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .empty-action {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8) !important;
          border: none !important;
          border-radius: 12px !important;
          padding: 0.75rem 1.5rem !important;
          font-weight: 600 !important;
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3) !important;
          transition: all 0.3s ease !important;
        }

        .empty-action:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 12px 30px rgba(59, 130, 246, 0.4) !important;
        }

        /* Grid View */
        .users-grid {
          min-height: 400px;
        }

        .grid-empty-state {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          text-align: center;
        }

        /* Pagination Styles */
        .pagination-card {
          background: ${
            isDark ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.95)"
          } !important;
          backdrop-filter: blur(20px) !important;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"} !important;
          border-radius: 20px !important;
          box-shadow: ${
            isDark
              ? "0 20px 40px rgba(0, 0, 0, 0.3)"
              : "0 20px 40px rgba(0, 0, 0, 0.08)"
          } !important;
        }

        .pagination-body {
          padding: 1.5rem 2rem !important;
        }

        .pagination-info {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .rows-per-page {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .rows-per-page .label {
          color: ${isDark ? "#e2e8f0" : "#374151"};
          font-weight: 500;
          font-size: 0.9rem;
        }

        .page-size-select {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.6)" : "rgba(248, 250, 252, 0.8)"
          } !important;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} !important;
          border-radius: 8px !important;
          color: ${isDark ? "#f1f5f9" : "#1e293b"} !important;
          padding: 0.5rem !important;
          width: 80px;
          font-size: 0.9rem;
        }

        .showing-info {
          color: ${isDark ? "#cbd5e1" : "#64748b"};
          font-size: 0.9rem;
        }

        .showing-info .highlight {
          color: ${isDark ? "#60a5fa" : "#3b82f6"};
          font-weight: 600;
        }

        .pagination-controls {
          display: flex;
          justify-content: flex-end;
        }

        .custom-pagination {
          gap: 0.25rem;
        }

        .custom-pagination .page-item {
          margin: 0;
        }

        .custom-pagination .page-link {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.6)" : "rgba(248, 250, 252, 0.8)"
          } !important;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} !important;
          color: ${isDark ? "#e2e8f0" : "#374151"} !important;
          border-radius: 8px !important;
          padding: 0.5rem 0.75rem !important;
          font-weight: 500 !important;
          transition: all 0.3s ease !important;
          margin: 0 0.125rem;
        }

        .custom-pagination .page-item.active .page-link {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8) !important;
          border-color: #3b82f6 !important;
          color: white !important;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
        }

        .custom-pagination .page-link:hover {
          background: ${
            isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)"
          } !important;
          border-color: ${
            isDark ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.2)"
          } !important;
          color: ${isDark ? "#93c5fd" : "#1d4ed8"} !important;
          transform: translateY(-1px);
        }

        .custom-pagination .page-item.disabled .page-link {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.3)" : "rgba(248, 250, 252, 0.5)"
          } !important;
          border-color: ${
            isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)"
          } !important;
          color: ${isDark ? "#64748b" : "#9ca3af"} !important;
          cursor: not-allowed;
        }

        /* Modal Styles */
        .custom-modal .modal-content {
          background: ${
            isDark ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)"
          } !important;
          backdrop-filter: blur(20px) !important;
          border: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"} !important;
          border-radius: 20px !important;
          box-shadow: ${
            isDark
              ? "0 25px 50px rgba(0, 0, 0, 0.5)"
              : "0 25px 50px rgba(0, 0, 0, 0.15)"
          } !important;
        }

        .modal-header-custom {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.6)" : "rgba(248, 250, 252, 0.8)"
          } !important;
          border-bottom: 1px solid
            ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"} !important;
          border-radius: 20px 20px 0 0 !important;
          padding: 1.5rem 2rem !important;
        }

        .modal-title-custom {
          color: ${isDark ? "#f1f5f9" : "#1e293b"} !important;
          font-weight: 600 !important;
          font-size: 1.25rem !important;
          display: flex;
          align-items: center;
        }

        .modal-body-custom {
          padding: 2rem !important;
          background: transparent;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: 1.5rem;
            text-align: center;
          }

          .header-content {
            flex-direction: column;
            gap: 1rem;
          }

          .header-stats {
            justify-content: center;
          }

          .action-controls {
            width: 100%;
            justify-content: center;
          }

          .view-toggle {
            flex: 1;
            max-width: 200px;
          }

          .filters-header {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }

          .pagination-info {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }

          .pagination-controls {
            justify-content: center;
          }

          .custom-pagination {
            flex-wrap: wrap;
            justify-content: center;
          }

          .action-buttons {
            justify-content: center;
          }

          .table-responsive {
            font-size: 0.85rem;
          }

          .table-cell {
            padding: 1rem 0.75rem !important;
          }

          .user-info {
            flex-direction: column;
            text-align: center;
            gap: 0.75rem;
          }

          .avatar-circle {
            width: 40px;
            height: 40px;
          }
        }

        @media (max-width: 576px) {
          .users-page {
            padding: 1rem !important;
          }

          .page-header {
            padding: 1.5rem;
          }

          .filters-body {
            padding: 1.5rem !important;
          }

          .pagination-body {
            padding: 1rem 1.5rem !important;
          }

          .header-icon {
            width: 48px;
            height: 48px;
            font-size: 1.25rem;
          }

          .page-title {
            font-size: 1.5rem;
          }

          .stat-value {
            font-size: 1.25rem;
          }

          .empty-icon {
            font-size: 3rem;
          }

          .filter-tag {
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
          }
        }

        /* Animation Enhancements */
        .users-page * {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .page-header {
          animation: slideInDown 0.6s ease-out;
        }

        .filters-card {
          animation: slideInUp 0.6s ease-out 0.1s both;
        }

        .data-table-card {
          animation: slideInUp 0.6s ease-out 0.2s both;
        }

        .pagination-card {
          animation: slideInUp 0.6s ease-out 0.3s both;
        }

        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .table-row {
          animation: fadeIn 0.4s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* Hover Effects */
        .create-btn,
        .apply-btn,
        .empty-action {
          position: relative;
          overflow: hidden;
        }

        .create-btn::before,
        .apply-btn::before,
        .empty-action::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
          );
          transition: left 0.5s;
        }

        .create-btn:hover::before,
        .apply-btn:hover::before,
        .empty-action:hover::before {
          left: 100%;
        }

        /* Focus States */
        .search-field:focus,
        .filter-select:focus,
        .page-size-select:focus {
          outline: none !important;
          border-color: ${isDark ? "#60a5fa" : "#3b82f6"} !important;
          box-shadow: 0 0 0 3px
            ${isDark ? "rgba(96, 165, 250, 0.1)" : "rgba(59, 130, 246, 0.1)"} !important;
        }

        .action-btn:focus {
          outline: none !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2) !important;
        }

        /* Loading States */
        .apply-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .apply-btn:disabled:hover {
          transform: none !important;
        }

        /* Print Styles */
        @media print {
          .users-page {
            background: white !important;
          }

          .action-controls,
          .filters-card,
          .pagination-card {
            display: none !important;
          }

          .data-table-card {
            box-shadow: none !important;
            border: 1px solid #ccc !important;
          }

          .table-row:hover {
            background: transparent !important;
          }
        }
      `}</style>
    </>
  );
};

export default UsersPage;
